'use client'

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'
import { getMinimalProfile, getProfileByAuthId } from '@/app/actions/data'
import { logger } from '@/lib/services/logger.service'
import { logImpersonationAudit, logTherapistAudit } from '@/app/actions/auth'

export interface UserProfile {
  id: string
  authId: string
  email: string | null
  firstName: string | null
  lastName: string | null
  timezone: string | null
  isAdmin: boolean | null
  isTherapist: boolean | null
  isSupervisor: boolean | null
  isSeniorSupervisor: boolean | null
  isBetweenSession: boolean | null
  isApprovedForCouples: boolean | null
  googleMeetLink: string | null
  isAcceptingIndividual: boolean | null
  isAcceptingCouples: boolean | null
  employmentStatus: 'EMPLOYEE' | 'CONTRACTOR' | 'RESIDENT' | 'BES' | null
  cohort: string | null
  payRate: number | null
  crpoRegistrationDate: string | null
}

interface UserContextType {
  profile: UserProfile | null
  isLoadingProfile: boolean
  profileError: Error | null
  fetchProfile: (authId: string) => Promise<UserProfile | null>
  refetchProfile: () => Promise<void>
  logout: () => Promise<void>

  // Impersonation
  impersonatedProfile: UserProfile | null
  actualProfile: UserProfile | null
  isImpersonating: boolean
  impersonate: (targetAuthId: string) => Promise<void>
  stopImpersonation: () => void
}

const UserContext = createContext<UserContextType | undefined>(undefined)

/**
 * User/Profile Context for Server-Side Auth
 *
 * Philosophy:
 * - No browser auth client usage (server-side auth only)
 * - Profile fetched from database after login (call fetchProfile with authId)
 * - Middleware handles auth validation
 * - Components call fetchProfile() to load user data
 */
export function UserProvider({ children }: { children: React.ReactNode }) {
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [isLoadingProfile, setIsLoadingProfile] = useState(false)
  const [profileError, setProfileError] = useState<Error | null>(null)

  // Impersonation state — purely in-memory React state. A page refresh
  // drops it (matches Schedule/Dashboard pattern: refresh = back to self).
  const [actualProfile, setActualProfile] = useState<UserProfile | null>(null)
  const [impersonatedProfile, setImpersonatedProfile] = useState<UserProfile | null>(null)

  /**
   * Fetch profile for authenticated user
   */
  const fetchProfile = useCallback(async (authId: string) => {
    try {
      setIsLoadingProfile(true)
      setProfileError(null)
      
      const profileData = await getMinimalProfile(authId)
      
      if (!profileData) {
        throw new Error('Profile not found')
      }
      
      const userProfile: UserProfile = {
        id: profileData.id,
        authId: authId,
        email: profileData.email,
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        timezone: profileData.timezone,
        isAdmin: profileData.isAdmin,
        isTherapist: profileData.isTherapist,
        isSupervisor: profileData.isSupervisor,
        isSeniorSupervisor: profileData.isSeniorSupervisor,
        isBetweenSession: profileData.isBetweenSession,
        isApprovedForCouples: profileData.isApprovedForCouples,
        googleMeetLink: profileData.googleMeetLink,
        isAcceptingIndividual: profileData.isAcceptingIndividual,
        isAcceptingCouples: profileData.isAcceptingCouples,
        employmentStatus: profileData.employmentStatus as 'EMPLOYEE' | 'CONTRACTOR' | 'RESIDENT' | 'BES' | null,
        cohort: profileData.cohort ?? null,
        payRate: profileData.payRate,
        crpoRegistrationDate: profileData.crpoRegistrationDate,
      }
      
      setProfile(userProfile)
      return userProfile
    } catch (error) {
      logger.error('Failed to fetch profile', error)
      setProfileError(error as Error)
      return null
    } finally {
      setIsLoadingProfile(false)
    }
  }, [])

  /**
   * Refetch current profile
   */
  const refetchProfile = useCallback(async () => {
    if (impersonatedProfile) {
      await fetchProfile(impersonatedProfile.authId)
    } else if (profile) {
      await fetchProfile(profile.authId)
    }
  }, [profile, impersonatedProfile, fetchProfile])

  /**
   * Logout user and redirect to login
   */
  const logout = useCallback(async () => {
    try {
      const response = await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include'
      })

      if (!response.ok) {
        throw new Error('Logout failed')
      }

      const data = await response.json()
      const ipAddress = data.ipAddress

      // Log logout for SOC 2 compliance (after logout to include IP)
      if (profile?.authId) {
        logTherapistAudit({
          proId: profile.authId,
          action: 'auth.logout',
          resource: 'session',
          ipAddress,
          details: {
            logoutType: 'manual',
            wasImpersonating: !!impersonatedProfile
          }
        }).catch(err => {
          logger.error('Failed to log logout audit', err)
        })
      }

      // Clear local state
      setProfile(null)
      setActualProfile(null)
      setImpersonatedProfile(null)

      // Redirect to login page
      window.location.href = '/login'
    } catch (error) {
      logger.error('Logout error', error)
      // Redirect anyway to ensure user is logged out
      window.location.href = '/login'
    }
  }, [profile, impersonatedProfile])

  /**
   * Impersonate another user (admin or supervisor)
   */
  const impersonate = useCallback(async (targetAuthId: string) => {
    try {
      if (!profile?.isAdmin && !profile?.isSupervisor && !profile?.isSeniorSupervisor && 
          profile?.id !== 'd0ed67a9-5d67-4cc7-8186-ddc4abb0cbd8') {
        throw new Error('Only admins, supervisors, and authorized users can impersonate')
      }
      
      // Store actual profile
      setActualProfile(profile)
      
      // Fetch target profile
      const targetProfile = await fetchProfile(targetAuthId)
      
      if (targetProfile) {
        setImpersonatedProfile(targetProfile)
        setProfile(targetProfile)

        // Log impersonation for audit
        const impersonatorRole = profile.isAdmin ? 'admin' : profile.isSeniorSupervisor ? 'senior_supervisor' : 'supervisor'
        logImpersonationAudit({
          actualProId: profile.authId,
          actualProName: `${profile.firstName} ${profile.lastName}`,
          targetProId: targetProfile.authId,
          targetProName: `${targetProfile.firstName} ${targetProfile.lastName}`,
          action: 'start',
          impersonatorRole
        }).catch(console.error)
      }
    } catch (error) {
      logger.error('Failed to impersonate', error)
      throw error
    }
  }, [profile, actualProfile, fetchProfile])

  /**
   * Stop impersonation
   */
  const stopImpersonation = useCallback(() => {
    if (actualProfile) {
      // Log stop impersonation
      if (impersonatedProfile) {
        const impersonatorRole = actualProfile.isAdmin ? 'admin' : actualProfile.isSeniorSupervisor ? 'senior_supervisor' : 'supervisor'
        logImpersonationAudit({
          actualProId: actualProfile.authId,
          actualProName: `${actualProfile.firstName} ${actualProfile.lastName}`,
          targetProId: impersonatedProfile.authId,
          targetProName: `${impersonatedProfile.firstName} ${impersonatedProfile.lastName}`,
          action: 'stop',
          impersonatorRole
        }).catch(console.error)
      }
      
      setProfile(actualProfile)
      setImpersonatedProfile(null)
      setActualProfile(null)
    }
  }, [actualProfile, impersonatedProfile])

  // Impersonation-flicker diagnostic — logs every effective-profile
  // change so we can trace Charlotte → Cherry flips on the Supervision
  // Hub. Dev-only; strip once the root cause is known.
  const effectiveProfile = impersonatedProfile || profile
  const lastLoggedIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return
    const id = effectiveProfile?.id ?? null
    if (lastLoggedIdRef.current === id) return
    lastLoggedIdRef.current = id
    // eslint-disable-next-line no-console
    console.log('[UserContext] effective profile changed', {
      t: new Date().toISOString().slice(11, 23),
      effective: effectiveProfile?.firstName ?? null,
      effectiveId: effectiveProfile?.id?.slice(0, 8) ?? null,
      innerProfile: profile?.firstName ?? null,
      impersonated: impersonatedProfile?.firstName ?? null,
      actual: actualProfile?.firstName ?? null,
    })
  })

  const value: UserContextType = {
    profile: effectiveProfile,
    isLoadingProfile,
    profileError,
    fetchProfile,
    refetchProfile,
    logout,
    impersonatedProfile,
    actualProfile,
    isImpersonating: !!impersonatedProfile,
    impersonate,
    stopImpersonation,
  }

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}

export function useUser() {
  const context = useContext(UserContext)
  if (context === undefined) {
    throw new Error('useUser must be used within UserProvider')
  }
  return context
}